<script setup>
import SettingsSection from './SettingsSection.vue';

// One editable list (subjects or grade levels): the current entries with a
// remove button each, and a search box to add more. Saved with the
// section's Save button.
defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  items: { type: Array, required: true }, // [{ id, title, meta }]
  available: { type: Array, required: true }, // options not yet in the list
  addLabel: { type: String, default: 'Add…' },
  emptyText: { type: String, default: 'Nothing added yet.' },
  dirty: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  savedAt: { type: Number, default: 0 },
});
const emit = defineEmits(['add', 'remove', 'save', 'reset']);
</script>

<template>
  <SettingsSection
    :title="title"
    :description="description"
    :dirty="dirty"
    :saving="saving"
    :saved-at="savedAt"
    @save="emit('save')"
    @reset="emit('reset')"
  >
    <div class="list">
      <ul v-if="items.length" class="list__rows">
        <li v-for="item in items" :key="item.id" class="list__row">
          <span class="list__title">{{ item.title }}</span>
          <span v-if="item.meta" class="list__meta">{{ item.meta }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="x-small"
            class="list__remove"
            :aria-label="`Remove ${item.title}`"
            @click="emit('remove', item)"
          />
        </li>
      </ul>
      <p v-else class="list__empty">{{ emptyText }}</p>

      <v-autocomplete
        :model-value="null"
        :items="available"
        item-title="title"
        item-value="id"
        :placeholder="addLabel"
        :disabled="!available.length"
        prepend-inner-icon="mdi-plus"
        hide-details
        no-data-text="No matches"
        @update:model-value="(id) => id && emit('add', id)"
      >
        <template #item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps" :subtitle="item.raw.meta || undefined" />
        </template>
      </v-autocomplete>
    </div>
  </SettingsSection>
</template>

<style scoped>
.list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.list__rows {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 10px;
  max-height: 320px;
  overflow-y: auto;
}
.list__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 4px 6px 4px 12px;
  font-size: 14px;
}
.list__row + .list__row {
  border-top: 1px solid var(--border);
}
.list__title {
  font-weight: 500;
}
.list__meta {
  flex: 1;
  font-size: 12px;
  color: var(--muted-foreground);
}
.list__remove {
  margin-left: auto;
  opacity: 0.6;
}
.list__remove:hover {
  opacity: 1;
}
.list__empty {
  margin: 0;
  padding: 16px;
  border: 1px dashed var(--border-strong);
  border-radius: 10px;
  font-size: 14px;
  color: var(--muted-foreground);
  text-align: center;
}
</style>
